import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Input,
  message,
  Modal,
  Row,
  Typography,
} from 'antd';
import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';

import { linkShortnerApi } from '../api/linkShortnerApi';
import { useLinkShortnerPrefix } from '../hooks/useLinkShortnerPrefix';
import { ShortenedLink } from '../typings/linkShortner';

const { Title, Text } = Typography;

interface LinkShortnerHeaderProps {
  isEditing: boolean;
  selectedLink?: ShortenedLink;
  cancelEdit?: () => void;
  createLink: (params: {
    slug?: string;
    destination: string;
    description: string;
  }) => Promise<ShortenedLink>;
  createLinkLoading: boolean;
  updateLink: (params: {
    id: string;
    slug?: string;
    destination?: string;
    description?: string;
  }) => Promise<ShortenedLink>;
  updateLinkLoading: boolean;
}

export const LinkShortnerHeader = memo(
  ({
    createLink,
    createLinkLoading,
    isEditing,
    selectedLink,
    cancelEdit,
    updateLink,
    updateLinkLoading,
  }: LinkShortnerHeaderProps) => {
    const [slug, setSlug] = useState('');
    const [destination, setDestination] = useState('');
    const [description, setDescription] = useState('');

    const [isLoadingShortenedLink, setIsLoadingShortenedLink] = useState(false);

    const linkShortnerPrefix = useLinkShortnerPrefix();

    const resetForm = () => {
      setSlug('');
      setDestination('');
      setDescription('');
    };

    const handleCreateLink = async () => {
      try {
        await createLink({
          slug: slug || undefined,
          destination,
          description,
        });
        setSlug('');
        setDestination('');
        setDescription('');
      } catch (err) {
        message.error('Não foi possivel criar o link. Tente novamente');
      }
    };

    const handleUpdateLink = async () => {
      if (!selectedLink) return;

      const data = {
        id: selectedLink.id,
        slug: slug || undefined,
        destination: destination || undefined,
        description: description || undefined,
      };
      try {
        Modal.confirm({
          title: 'Atenção',
          icon: <ExclamationCircleOutlined />,
          content:
            'Deseja realmente atualizar o link selecionado? Essa ação não pode ser desfeita.',
          okText: 'Sim',
          cancelText: 'Não',
          onOk: () => {
            updateLink(data);
            resetForm();
            cancelEdit?.();
            message.success('Link atualizado com sucesso!');
          },
        });
      } catch (err) {
        message.error(
          'Não foi possível atualizar o link. Tente novamente ' + err
        );
      }
    };

    const shortenLink = async () => {
      setIsLoadingShortenedLink(true);
      let slugExists = false;
      if (slug) {
        slugExists = await linkShortnerApi.checkIfLinkExistsBySlug(slug);
      }
      if (slugExists) {
        message.error('O link já existe, por favor escolha outro!');
        setIsLoadingShortenedLink(false);
        return;
      }

      if (isEditing) {
        await handleUpdateLink();
        setIsLoadingShortenedLink(false);
        return;
      }

      await handleCreateLink();
      setIsLoadingShortenedLink(false);
    };

    useEffect(() => {
      if (isEditing && selectedLink) {
        setSlug(selectedLink.slug);
        setDestination(selectedLink.destination);
        setDescription(selectedLink.description);
      }
    }, [isEditing, selectedLink]);

    const handleCancelEdit = () => {
      cancelEdit?.();
      resetForm();
    };

    return (
      <>
        {isEditing ? (
          <Title level={3}>Encurtador de links - Editar</Title>
        ) : (
          <Title level={3}>Encurtador de links</Title>
        )}
        <Divider style={{ marginBlock: '40px' }} />
        <Row
          style={{ marginBottom: '24px', alignItems: 'flex-end' }}
          gutter={[16, 0]}
        >
          <Col>
            <Text style={{ fontSize: '24px' }}>{linkShortnerPrefix}</Text>
          </Col>
          <Col flex={1}>
            <UnderlineInput
              placeholder='Digite seu link personalizado (opcional)'
              value={slug}
              onChange={event => setSlug(event.target.value)}
            />
          </Col>
        </Row>
        <Row style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Input
              placeholder='Cole aqui o link de destino'
              value={destination}
              onChange={event => setDestination(event.target.value)}
            />
          </Col>
        </Row>
        <Row style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Input
              placeholder='Digite a descrição do seu link'
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </Col>
        </Row>
        {isEditing ? (
          <>
            <Row justify='end' gutter={16}>
              <Col>
                <Button type='default' onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              </Col>
              <Col>
                <Button
                  type='primary'
                  onClick={shortenLink}
                  loading={updateLinkLoading || isLoadingShortenedLink}
                >
                  Atualizar link
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <Row justify='end'>
            <Button
              type='primary'
              onClick={shortenLink}
              loading={createLinkLoading || isLoadingShortenedLink}
            >
              Encurtar link
            </Button>
          </Row>
        )}
      </>
    );
  }
);

const UnderlineInput = styled(Input)`
  border-top-width: 0px;
  border-left-width: 0px;
  border-right-width: 0px !important;
  padding-inline: 8px;
`;
